import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert, alpha, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, IconButton, InputAdornment,
  Menu, MenuItem, Snackbar, Stack, Switch, TextField, ToggleButton, ToggleButtonGroup, Typography
} from '@mui/material';
import { MoreVert as MoreIcon } from '@mui/icons-material';
import { createDataExport, deleteDataExport, getDataExportCiSnippets, listDataExports, runDataExportNow, toggleDataExport } from 'api/features';
import { downloadAuthed } from 'api/download';
import { GREEN, MONO } from './scanUi';
import {
  DAYS, FILTERS, FORMAT_META, FREQUENCIES, NETWORKS, SCOPES, SEVERITIES, STARTERS, STATUS, cutShort, expiry, filterExports, isRunning, needsAttention,
  nextRuns, scheduleText, scopeText, statusOf
} from './exportLogic';

const panel = { borderRadius: 3, border: 1, borderColor: 'divider', bgcolor: 'background.paper' };
const BLANK = { name: '', format: 'pdf', scope: 'All findings', severity: 'All severities', network: '', enabled: false, frequency: 'weekly', day: 0, time: '08:00', retention_days: 30, sarif_category: 'fuzzscan' };

const Badge = ({ format }) => {
  const m = FORMAT_META[format] || { label: String(format).toUpperCase(), color: '#94a3b8' };
  return <Box sx={{ width: 50, height: 34, borderRadius: 2, flex: 'none', display: 'grid', placeItems: 'center', fontWeight: 800, fontSize: 12, bgcolor: alpha(m.color, 0.16), color: m.color }}>{m.label}</Box>;
};

export default function DataExportPage() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [busy, setBusy] = useState('');
  const [form, setForm] = useState(null);
  const [menu, setMenu] = useState(null); // { anchor, item }
  const [ci, setCi] = useState(null);
  const [toast, setToast] = useState(null);
  const say = (severity, message) => setToast({ severity, message });

  const load = useCallback(async () => {
    try { const r = await listDataExports({ ordering: '-updated_at' }); setList(Array.isArray(r) ? r : r.results || []); } catch (e) { say('error', e?.message || 'Could not load your reports.'); }
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);
  const running = list.some(isRunning);
  useEffect(() => {
    if (!running) return undefined;
    const t = setInterval(load, 4000);
    return () => clearInterval(t);
  }, [running, load]);

  const shown = useMemo(() => filterExports(list, filter), [list, filter]);
  const next = useMemo(() => nextRuns(list), [list]);

  const act = async (id, fn, ok) => {
    setBusy(String(id));
    try { await fn(); if (ok) say('success', ok); await load(); } catch (e) { say('error', e?.message || 'That did not work.'); }
    setBusy('');
  };
  const create = (payload, ok) => act('new', () => createDataExport(payload), ok);
  const saveForm = async () => {
    const f = form;
    await create({
      name: f.name.trim() || `${f.scope} ${f.format.toUpperCase()} report`, format: f.format, scope: f.scope, enabled: f.enabled, frequency: f.frequency,
      run_at: f.time, day_of_week: Number(f.day), timezone_name: Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC', retention_days: Number(f.retention_days) || 30,
      purge_after_expiry: true, ci_provider: 'none', sarif_category: f.format === 'sarif' ? f.sarif_category : '', options: { severity_filter: f.severity, ...(f.network ? { network_scope: f.network } : {}) }
    }, f.enabled ? 'Scheduled. The first file is being made now.' : 'Making your report.');
    setForm(null);
  };
  const showCi = async (item) => { try { setCi({ item, snippets: await getDataExportCiSnippets(item.id) }); } catch (e) { say('error', e?.message || 'Could not load the CI snippets.'); } };

  return (
    <Box sx={{ p: { xs: 2, md: 4 }, maxWidth: 1500, mx: 'auto' }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2} sx={{ mb: 3 }}>
        <Box>
          <Typography variant="h4" fontWeight={700}>Export center</Typography>
          <Typography color="text.secondary">Pick what you need and get it in one click. Schedule it if you want it again and again</Typography>
        </Box>
        <Button variant="outlined" color="inherit" onClick={() => setForm({ ...BLANK })} sx={{ height: 44, fontWeight: 600 }}>Custom report</Button>
      </Stack>

      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))', gap: 2, mb: 3 }}>
        {STARTERS.map((s) => (
          <Box key={s.key} sx={{ ...panel, p: 2.2, display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Badge format={s.payload.format} />
            <Typography fontWeight={700}>{s.title}</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ flex: 1, lineHeight: 1.5 }}>{s.text}</Typography>
            <Button size="small" disabled={busy === 'new'} onClick={() => create(s.payload, `Making "${s.title}".`)} sx={{ alignSelf: 'flex-start', px: 0, color: GREEN, fontWeight: 700 }}>Create now →</Button>
          </Box>
        ))}
      </Box>

      {loading && <Box sx={{ textAlign: 'center', py: 5 }}><CircularProgress sx={{ color: GREEN }} /></Box>}
      {!loading && (
        <Box sx={{ display: 'flex', gap: 2.5, flexWrap: 'wrap', alignItems: 'flex-start' }}>
          <Box sx={{ ...panel, flex: '2 1 640px', minWidth: 0, overflow: 'hidden' }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1} sx={{ p: 2.5 }}>
              <Typography fontWeight={600}>Your reports</Typography>
              <ToggleButtonGroup exclusive size="small" value={filter} onChange={(_, v) => v && setFilter(v)}>
                {FILTERS.map((f) => <ToggleButton key={f.id} value={f.id} sx={{ textTransform: 'none', px: 1.6, borderRadius: '999px !important', fontWeight: 600 }}>{f.label} {filterExports(list, f.id).length}</ToggleButton>)}
              </ToggleButtonGroup>
            </Stack>
            {!shown.length && <Typography color="text.secondary" sx={{ px: 2.5, pb: 3 }}>{list.length ? 'No report matches.' : 'No reports yet. Pick one of the starters above.'}</Typography>}
            {shown.map((e) => {
              const st = STATUS[statusOf(e)];
              const ex = expiry(e);
              return (
                <Stack key={e.id} direction="row" alignItems="center" flexWrap="wrap" gap={2} sx={{ px: 2.5, py: 1.8, borderTop: 1, borderColor: 'divider' }}>
                  <Badge format={e.format} />
                  <Box sx={{ flex: '1 1 240px', minWidth: 0 }}>
                    <Typography fontWeight={600} noWrap>{e.name}</Typography>
                    <Typography variant="body2" color="text.secondary" noWrap>{scopeText(e)} · {scheduleText(e)}</Typography>
                    {cutShort(e) && <Typography variant="caption" sx={{ color: '#f59e0b' }}>Cut short at 5,000 findings. Narrow the scope to get everything.</Typography>}
                    {needsAttention(e) && e.last_error && <Typography variant="caption" sx={{ color: '#ef4444', display: 'block' }} noWrap>{e.last_error}</Typography>}
                  </Box>
                  <Box sx={{ width: 150 }}>
                    <Typography variant="caption" color="text.secondary">{ex.text || (statusOf(e) === 'running' ? 'Working on it…' : '')}</Typography>
                    {ex.text && <Box sx={{ height: 5, borderRadius: 1, bgcolor: alpha('#fff', 0.08), mt: 0.5 }}><Box sx={{ height: '100%', width: `${ex.pct}%`, borderRadius: 1, bgcolor: GREEN }} /></Box>}
                  </Box>
                  <Typography sx={{ fontSize: 11, fontWeight: 700, px: 1.2, py: 0.5, borderRadius: 1.5, minWidth: 78, textAlign: 'center', bgcolor: alpha(st.color, 0.16), color: st.color }}>{st.label}</Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ width: 62, textAlign: 'right', fontFamily: MONO }}>{e.file_size || '-'}</Typography>
                  <Stack direction="row" gap={1} alignItems="center">
                    <Button size="small" variant="outlined" color="inherit" disabled={!e.download_url} onClick={() => downloadAuthed(e.download_url).catch((err) => say('error', err.message))}>Download</Button>
                    <Button size="small" variant="outlined" color="inherit" disabled={busy === String(e.id) || isRunning(e)} onClick={() => act(e.id, () => runDataExportNow(e.id), 'Making a fresh file.')}>Run now</Button>
                    <IconButton size="small" onClick={(ev) => setMenu({ anchor: ev.currentTarget, item: e })}><MoreIcon fontSize="small" /></IconButton>
                  </Stack>
                </Stack>
              );
            })}
          </Box>

          <Box sx={{ ...panel, flex: '1 1 380px', minWidth: 0, p: 2.5 }}>
            <Typography fontWeight={600} sx={{ mb: 1.5 }}>What runs next</Typography>
            {!next.length && <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>Nothing is scheduled. Use Custom report to make a report that builds itself every day, week or month.</Typography>}
            {next.map((n) => (
              <Stack key={n.id} direction="row" gap={1.5} alignItems="center" sx={{ py: 1.4, borderTop: 1, borderColor: 'divider' }}>
                <Box sx={{ width: 92 }}>
                  <Typography variant="body2" sx={{ fontWeight: 700, color: GREEN }}>{n.day}</Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ fontFamily: MONO }}>{n.time}</Typography>
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography fontWeight={600} noWrap>{n.name}</Typography>
                  <Typography variant="caption" color={n.failed ? '#ef4444' : 'text.secondary'}>{n.detail}{n.failed ? ' · last run failed' : ''}</Typography>
                </Box>
                <Switch checked disabled={busy === String(n.id)} onChange={() => act(n.id, () => toggleDataExport(n.id, false), 'Schedule turned off.')} sx={{ '& .Mui-checked': { color: GREEN }, '& .Mui-checked + .MuiSwitch-track': { bgcolor: GREEN } }} />
              </Stack>
            ))}
            <Button size="small" onClick={() => setForm({ ...BLANK, enabled: true })} sx={{ mt: 1, px: 0, color: GREEN, fontWeight: 700 }}>+ New scheduled report</Button>
          </Box>
        </Box>
      )}

      <Menu anchorEl={menu?.anchor} open={!!menu} onClose={() => setMenu(null)}>
        {menu && menu.item.enabled === false && <MenuItem onClick={() => { const i = menu.item; setMenu(null); act(i.id, () => toggleDataExport(i.id, true), 'Scheduled to repeat.'); }}>Turn on its schedule</MenuItem>}
        {menu && menu.item.format === 'sarif' && <MenuItem onClick={() => { const i = menu.item; setMenu(null); showCi(i); }}>Show CI snippets</MenuItem>}
        {menu && <MenuItem sx={{ color: '#ef4444' }} onClick={() => { const i = menu.item; setMenu(null); if (window.confirm(`Delete "${i.name}" and its file?`)) act(i.id, () => deleteDataExport(i.id), 'Report deleted.'); }}>Delete</MenuItem>}
      </Menu>

      <Dialog open={!!form} onClose={() => setForm(null)} fullWidth maxWidth="sm">
        {form && <>
          <DialogTitle>{form.enabled ? 'New scheduled report' : 'Custom report'}</DialogTitle>
          <DialogContent>
            <Stack gap={2} sx={{ mt: 1 }}>
              <TextField label="Name (optional)" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} fullWidth />
              <Stack direction="row" gap={2}>
                <TextField select label="File" value={form.format} onChange={(e) => setForm({ ...form, format: e.target.value })} fullWidth>{Object.entries(FORMAT_META).map(([v, m]) => <MenuItem key={v} value={v}>{m.label}</MenuItem>)}</TextField>
                <TextField select label="What to include" value={form.scope} onChange={(e) => setForm({ ...form, scope: e.target.value })} fullWidth>{SCOPES.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}</TextField>
              </Stack>
              <TextField select label="How serious" value={form.severity} onChange={(e) => setForm({ ...form, severity: e.target.value })}>{SEVERITIES.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}</TextField>
              <TextField select label="Which network" value={form.network} onChange={(e) => setForm({ ...form, network: e.target.value })} helperText="For imported scans: where they were run from">{NETWORKS.map((n) => <MenuItem key={n.id || 'both'} value={n.id}>{n.label}</MenuItem>)}</TextField>
              <FormControlLabel control={<Switch checked={form.enabled} onChange={(e) => setForm({ ...form, enabled: e.target.checked })} sx={{ '& .Mui-checked': { color: GREEN }, '& .Mui-checked + .MuiSwitch-track': { bgcolor: GREEN } }} />} label="Build it again on a schedule" />
              {form.enabled && (
                <Stack direction="row" gap={2} flexWrap="wrap">
                  <TextField select label="How often" value={form.frequency} onChange={(e) => setForm({ ...form, frequency: e.target.value })} sx={{ flex: 1, minWidth: 150 }}>{FREQUENCIES.map((f) => <MenuItem key={f.value} value={f.value}>{f.label}</MenuItem>)}</TextField>
                  {form.frequency === 'weekly' && <TextField select label="On" value={form.day} onChange={(e) => setForm({ ...form, day: e.target.value })} sx={{ flex: 1, minWidth: 130 }}>{DAYS.map((d, i) => <MenuItem key={d} value={i}>{d}</MenuItem>)}</TextField>}
                  <TextField type="time" label={form.frequency === 'hourly' ? 'At minute' : 'At'} value={form.time} onChange={(e) => setForm({ ...form, time: e.target.value })} sx={{ flex: 1, minWidth: 130 }} InputLabelProps={{ shrink: true }} />
                </Stack>
              )}
              <TextField type="number" label="Keep the file for" value={form.retention_days} onChange={(e) => setForm({ ...form, retention_days: e.target.value })} InputProps={{ endAdornment: <InputAdornment position="end">days</InputAdornment> }} helperText="After that the file is deleted. You can always make a new one." />
            </Stack>
          </DialogContent>
          <DialogActions sx={{ p: 2.5 }}>
            <Button color="inherit" onClick={() => setForm(null)}>Cancel</Button>
            <Button variant="contained" disabled={busy === 'new'} onClick={saveForm} sx={{ fontWeight: 700, bgcolor: GREEN, color: '#06220F', '&:hover': { bgcolor: '#16a34a' } }}>Create report</Button>
          </DialogActions>
        </>}
      </Dialog>

      <Dialog open={!!ci} onClose={() => setCi(null)} fullWidth maxWidth="md">
        {ci && <>
          <DialogTitle>Use "{ci.item.name}" in your pipeline</DialogTitle>
          <DialogContent>
            {[['github', 'GitHub Actions'], ['gitlab', 'GitLab CI'], ['curl', 'Download with curl (set FUZZSCAN_TOKEN first)']].filter(([k]) => ci.snippets[k]).map(([k, label]) => (
              <Box key={k} sx={{ mb: 2 }}>
                <Typography variant="overline" color="text.secondary">{label}</Typography>
                <Box component="pre" sx={{ m: 0, p: 1.5, borderRadius: 2, bgcolor: alpha('#000', 0.3), fontFamily: MONO, fontSize: 12.5, whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>{ci.snippets[k]}</Box>
              </Box>
            ))}
          </DialogContent>
          <DialogActions sx={{ p: 2.5 }}><Button onClick={() => setCi(null)}>Close</Button></DialogActions>
        </>}
      </Dialog>
      <Snackbar open={!!toast} autoHideDuration={4000} onClose={() => setToast(null)}>{toast ? <Alert severity={toast.severity} variant="filled">{toast.message}</Alert> : undefined}</Snackbar>
    </Box>
  );
}
